/**
 * contentEditable DOM 序列化 / 选区偏移 / mention 节点操作
 *
 * ## 两条路径，不可混用
 *
 * **编辑态（live edit）** — DOM 是唯一真相：
 * - mention 区间：`domMentionBounds.collectDomMentionRanges` / `isOffsetInsideDomMention`
 * - 删除 / 触发检测 / 选区映射：只走 DOM token，禁止对 serialize 串做启发式 `@`/`/` 解析
 *
 * **加载态（hydrate）** — 仅在外部 value 注入时（历史编辑、预填、语音覆盖）：
 * - `buildEditorFromSerialized` + `parseSerializedMentions`（紧凑 `@body`/`/body` 格式）
 * - 由 `needsEditorHydration` + `lastEmittedRef` 守卫，编辑过程中不触发
 */
import type { TMentionEditorKind } from './types';
import type { ISelectedKnowledgeTagData } from '../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import type { ISelectedSkillTagData } from '../SkillMention/SelectedSkillTag';
import { buildKnowledgeInlineLabel } from '../utils/inlineMentionLabel';
import { resolveSelectedSkillDisplayName } from '../SkillMention/SelectedSkillTag';
import {
  mentionEndOffset,
} from '../utils/inlineMentionEditing';
import type { IInlineMentionRange } from '../utils/inlineMentionRanges';
import { MENTION_BOUNDARY_MARKER } from '../utils/inlineMentionRanges';
import {
  createMentionElement,
  parseMentionBodyFromLabel,
  normalizeSerializedMentionGaps,
  parseSerializedMentions,
  serializeMentionBody,
} from './mentionTokenDom';
import { buildKnowledgeMentionKey } from './mentionKeyUtils';
import { detectMentionScript } from '../mentionShared/mentionText';
import { collectDomMentionRanges } from './domMentionBounds';

export { collectDomMentionRanges, isOffsetInsideDomMention } from './domMentionBounds';

const MENTION_KIND_ATTR = 'data-mention-kind';
const MENTION_BODY_ATTR = 'data-mention-body';
const MENTION_KEY_ATTR = 'data-mention-key';
const MENTION_PAYLOAD_ATTR = 'data-mention-payload';
const MENTION_TOKEN_CLASS = 'dba-mention-token';

export interface IEditorLocaleContext {
  kbDocUnit: string;
  isEN: boolean;
}

export {
  createMentionElement,
  serializeMentionBody,
  parseMentionBodyFromLabel,
  parseSerializedMentions,
  normalizeSerializedMentionGaps,
  buildKnowledgeMentionKey,
};

function stripZeroWidthChars(text: string): string {
  return text.replace(/\u200b/g, '');
}

function getTextSerializedLength(text: string): number {
  return stripZeroWidthChars(text).length;
}

function isMentionToken(node: Node | null): boolean {
  if (!(node instanceof HTMLElement)) return false;
  const kind = node.getAttribute(MENTION_KIND_ATTR);
  return (
    node.classList.contains(MENTION_TOKEN_CLASS)
    && (kind === 'knowledge' || kind === 'skill')
    && node.hasAttribute(MENTION_BODY_ATTR)
  );
}

function removeTrailingSpaceAfterMention(el: HTMLElement): void {
  const next = el.nextSibling;
  if (next?.nodeType !== Node.TEXT_NODE) return;
  const text = next.textContent || '';
  if (!text.startsWith(' ')) return;
  const rest = text.slice(1);
  if (!rest) {
    next.remove();
    return;
  }
  next.textContent = rest;
}

/** 删除 mention 时同步清理前置 typing gap，避免连续删除后残留多个空格 */
function removeLeadingSpaceBeforeMention(el: HTMLElement): void {
  const prev = el.previousSibling;
  if (prev?.nodeType !== Node.TEXT_NODE) return;
  const text = prev.textContent || '';
  const plain = stripZeroWidthChars(text);

  if (!plain.replace(/\s/g, '')) {
    prev.remove();
    return;
  }

  if (text.endsWith(' ')) {
    const rest = text.slice(0, -1);
    if (!stripZeroWidthChars(rest)) {
      prev.remove();
      return;
    }
    prev.textContent = rest;
  }
}

function detachMentionToken(el: HTMLElement): void {
  removeLeadingSpaceBeforeMention(el);
  removeTrailingSpaceAfterMention(el);
  el.remove();
}

function removeDomMentionToken(root: HTMLElement, mentionKey: string): boolean {
  const el = Array.from(
    root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`),
  ).find((token) => token.getAttribute(MENTION_KEY_ATTR) === mentionKey);
  if (!el) return false;
  detachMentionToken(el);
  return true;
}

export function removeMentionByKey(root: HTMLElement, mentionKey: string): void {
  if (!mentionKey) return;
  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    if (el.getAttribute(MENTION_KEY_ATTR) === mentionKey) {
      detachMentionToken(el);
    }
  });
}

export function shortenKnowledgeMentionInRoot(
  root: HTMLElement,
  mentionKey: string,
  shortenedBody: string,
): void {
  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    if (el.getAttribute(MENTION_KEY_ATTR) !== mentionKey) return;
    if (getMentionKind(el) !== 'knowledge') return;
    el.setAttribute(MENTION_BODY_ATTR, shortenedBody);
    const labelEl = el.querySelector('.dba-mention-token__label');
    if (labelEl) labelEl.textContent = shortenedBody;
  });
}

/** 清理 mention 后紧邻的孤立触发符（如 `/` 未被 range 删除时） */
export function cleanupOrphanTriggerAfterMention(
  mention: HTMLElement,
  triggerChar: string,
): void {
  const sibling = mention.nextSibling;
  if (sibling?.nodeType !== Node.TEXT_NODE) return;
  const text = sibling.textContent || '';
  if (!text.startsWith(triggerChar)) return;
  const rest = text.slice(1);
  if (!rest) {
    sibling.remove();
    return;
  }
  sibling.textContent = rest;
}

function getMentionKind(node: HTMLElement): TMentionEditorKind | null {
  const kind = node.getAttribute(MENTION_KIND_ATTR);
  if (kind === 'knowledge' || kind === 'skill') return kind;
  return null;
}

function getMentionSerializedLength(node: HTMLElement): number {
  const kind = getMentionKind(node);
  if (!kind) return 0;
  const body = node.getAttribute(MENTION_BODY_ATTR) || '';
  return serializeMentionBody(body, kind).length;
}

/** 外部重置（清空、语音覆盖等）：仅写入纯文本，不反解析 mention */
export function setEditorPlainText(root: HTMLElement, text: string): void {
  root.innerHTML = '';
  if (!text) return;
  root.appendChild(document.createTextNode(text));
}

function ensureEmptyEditorCaret(root: HTMLElement): void {
  root.innerHTML = '';
  root.appendChild(document.createElement('br'));
}

/** 删除行首孤立 <br> / 空白文本，避免 mention 被挤到第二行、placeholder 错位 */
export function stripLeadingEditorEmpties(root: HTMLElement): boolean {
  let changed = false;

  while (root.firstChild) {
    const first = root.firstChild;
    if (first instanceof HTMLBRElement) {
      first.remove();
      changed = true;
      continue;
    }
    if (first.nodeType === Node.TEXT_NODE) {
      const text = first.textContent || '';
      if (!stripZeroWidthChars(text).replace(/\s/g, '')) {
        first.remove();
        changed = true;
        continue;
      }
    }
    break;
  }

  if (root.childNodes.length === 0) {
    ensureEmptyEditorCaret(root);
    changed = true;
  }

  return changed;
}

/** 序列化结果是否完全为空 */
export function isEditorValueEmpty(value: string): boolean {
  return !value.trim();
}

/** mention 后唯一尾随空格不算用户正文 */
function isIgnorableEditorTextNode(text: string): boolean {
  const stripped = stripZeroWidthChars(text);
  return !stripped || stripped === ' ';
}

/**
 * 是否展示 placeholder（DOM 优先，避免 React value 滞后导致与输入重叠）
 * - 有任意非忽略文本节点（含 `/`、`@`、标签前输入）→ 隐藏
 * - 仅 mention 或空 → 展示
 */
export function shouldShowEditorPlaceholder(
  root: HTMLElement | null,
  serialized: string,
): boolean {
  if (root) {
    for (const node of root.childNodes) {
      if (isEditorInlinePlaceholderNode(node)) continue;
      if (node.nodeType !== Node.TEXT_NODE) continue;
      if (!isIgnorableEditorTextNode(node.textContent || '')) return false;
    }
  }

  if (!serialized.trim()) return true;

  const plainText = parseSerializedMentions(serialized)
    .filter((segment) => segment.type === 'text')
    .map((segment) => segment.text)
    .join('');
  return !plainText.trim();
}


const EDITOR_INLINE_PLACEHOLDER_CLASS = 'dba-mention-editor-inline-placeholder';

function isEditorInlinePlaceholderNode(node: Node): boolean {
  return (
    node instanceof HTMLElement
    && node.classList.contains(EDITOR_INLINE_PLACEHOLDER_CLASS)
  );
}

/** 行首连续 mention 的最后一枚（placeholder 锚点） */
function findLastLeadingMentionAnchor(root: HTMLElement): HTMLElement | null {
  let anchor: HTMLElement | null = null;
  let foundLeadingMention = false;

  for (const node of root.childNodes) {
    if (isEditorInlinePlaceholderNode(node)) continue;
    if (isMentionToken(node)) {
      foundLeadingMention = true;
      anchor = node as HTMLElement;
      continue;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      if (foundLeadingMention && isIgnorableEditorTextNode(node.textContent || '')) {
        continue;
      }
      break;
    }
    break;
  }

  return anchor;
}

export function hasLeadingMentionElements(root: HTMLElement): boolean {
  return findLastLeadingMentionAnchor(root) !== null;
}

/** 序列化值是否仅含行首 mention（无用户正文） */
export function hasOnlyLeadingMentionsInValue(serialized: string): boolean {
  if (!serialized.trim()) return false;
  const segments = parseSerializedMentions(serialized);
  if (!segments.some((segment) => segment.type === 'mention')) return false;
  const plainText = segments
    .filter((segment) => segment.type === 'text')
    .map((segment) => segment.text)
    .join('');
  return !plainText.trim();
}

function placeCaretInTextNode(textNode: Text, offset: number): void {
  const range = document.createRange();
  range.setStart(textNode, offset);
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/** 光标落在标签后、placeholder 前的空格中 */
export function placeCaretBeforeInlinePlaceholder(root: HTMLElement): void {
  const placeholder = root.querySelector(`.${EDITOR_INLINE_PLACEHOLDER_CLASS}`);
  if (!placeholder) return;

  const prev = placeholder.previousSibling;
  if (prev?.nodeType === Node.TEXT_NODE) {
    const text = prev.textContent || '';
    placeCaretInTextNode(prev as Text, text.startsWith(' ') ? 1 : text.length);
    return;
  }

  const range = document.createRange();
  range.setStartBefore(placeholder);
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/** 行首有 mention 时：inline placeholder 参与文档流，实现文字环绕 tag */
export function syncEditorInlinePlaceholder(
  root: HTMLElement,
  text: string,
): void {
  root.querySelector(`.${EDITOR_INLINE_PLACEHOLDER_CLASS}`)?.remove();

  const anchor = findLastLeadingMentionAnchor(root);
  if (!anchor) return;

  const shouldPlaceCaret = document.activeElement === root;
  const caretNode = ensureTypingSpaceAfterMention(root, anchor, shouldPlaceCaret);
  if (!caretNode) return;

  const el = document.createElement('span');
  el.className = EDITOR_INLINE_PLACEHOLDER_CLASS;
  el.setAttribute('contenteditable', 'false');
  el.setAttribute('data-editor-placeholder', 'true');
  el.textContent = text;

  if (caretNode.nextSibling) {
    root.insertBefore(el, caretNode.nextSibling);
  } else {
    root.appendChild(el);
  }

  if (shouldPlaceCaret) {
    placeCaretBeforeInlinePlaceholder(root);
  }
}

export function removeEditorInlinePlaceholder(root: HTMLElement): boolean {
  const el = root.querySelector(`.${EDITOR_INLINE_PLACEHOLDER_CLASS}`);
  if (!el) return false;
  el.remove();
  return true;
}

/** 从提交/历史纯文本还原编辑器 DOM（含 mention tag） */
export function buildEditorFromSerialized(root: HTMLElement, text: string): void {
  root.innerHTML = '';
  if (!text) {
    ensureEmptyEditorCaret(root);
    return;
  }

  const segments = parseSerializedMentions(normalizeSerializedMentionGaps(text));
  segments.forEach((segment) => {
    if (segment.type === 'text') {
      if (segment.text) root.appendChild(document.createTextNode(segment.text));
      return;
    }
    root.appendChild(
      createMentionElement(segment.body, segment.kind, segment.mentionKey),
    );
  });
  syncMentionTypingGapsInRoot(root);
  stripLeadingEditorEmpties(root);
}

export function needsEditorHydration(root: HTMLElement, value: string): boolean {
  if (!value) {
    if (root.childNodes.length === 0) return true;
    return !(
      root.childNodes.length === 1 &&
      root.firstChild instanceof HTMLBRElement
    );
  }

  const domText = normalizeSerializedMentionGaps(serializeEditorRoot(root));
  const normalizedValue = normalizeSerializedMentionGaps(value);
  if (domText !== normalizedValue) return true;

  const hasMentionSegments = parseSerializedMentions(normalizedValue).some(
    (segment) => segment.type === 'mention',
  );
  if (!hasMentionSegments) return false;

  return !root.querySelector(`.${MENTION_TOKEN_CLASS}`);
}

/** 将编辑器 DOM 序列化为提交用纯文本 */
export function serializeEditorRoot(root: HTMLElement): string {
  let result = '';

  const appendGapBeforeChunk = (): void => {
    if (!result.length) return;
    if (/\s$/.test(result)) return;
    result += ' ';
  };

  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = stripZeroWidthChars(node.textContent || '');
      if (!text) return;
      // mention 边界符后接用户正文：DOM 里可能没有空格文本节点，提交时补上
      if (
        result.endsWith(MENTION_BOUNDARY_MARKER)
        && !/^\s/.test(text)
      ) {
        result += ' ';
      }
      result += text;
      return;
    }
    if (!(node instanceof HTMLElement)) return;

    if (isEditorInlinePlaceholderNode(node)) return;

    if (isMentionToken(node)) {
      const kind = getMentionKind(node)!;
      const body = node.getAttribute(MENTION_BODY_ATTR) || '';
      const serialized = serializeMentionBody(body, kind);
      if (!serialized) return;
      // 用户正文或上一 mention 后紧跟下一 mention：补空格（边界仍靠 \u200b，不靠空格）
      appendGapBeforeChunk();
      result += serialized;
      return;
    }
    if (node.tagName === 'BR') {
      result += '\n';
      return;
    }
    node.childNodes.forEach(walk);
  };

  root.childNodes.forEach(walk);
  return normalizeSerializedMentionGaps(result);
}

function getOffsetAtPoint(
  root: HTMLElement,
  container: Node,
  offset: number,
): number {
  let counted = 0;
  let found = false;

  const walk = (node: Node): void => {
    if (found) return;

    if (node === container) {
      if (node.nodeType === Node.TEXT_NODE) {
        counted += getTextSerializedLength(
          (node.textContent || '').slice(0, offset),
        );
      } else if (node instanceof HTMLElement && isMentionToken(node)) {
        counted += getMentionSerializedLength(node);
      }
      found = true;
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (node.contains(container)) {
        found = true;
        return;
      }
      counted += getTextSerializedLength(text);
      return;
    }

    if (!(node instanceof HTMLElement)) return;

    if (isEditorInlinePlaceholderNode(node)) return;

    if (isMentionToken(node)) {
      counted += getMentionSerializedLength(node);
      return;
    }
    if (node.tagName === 'BR') {
      counted += 1;
      return;
    }

    node.childNodes.forEach(walk);
  };

  if (container === root) {
    return offset;
  }

  root.childNodes.forEach(walk);
  return counted;
}

export function getSelectionOffsets(
  root: HTMLElement,
): { start: number; end: number; } {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) {
    return { start: 0, end: 0 };
  }
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) {
    return { start: 0, end: 0 };
  }
  return {
    start: getOffsetAtPoint(root, range.startContainer, range.startOffset),
    end: getOffsetAtPoint(root, range.endContainer, range.endOffset),
  };
}

export function setSelectionOffsets(
  root: HTMLElement,
  start: number,
  end: number,
): void {
  const sel = window.getSelection();
  if (!sel) return;

  let counted = 0;
  let startNode: Node | null = null;
  let startOff = 0;
  let endNode: Node | null = null;
  let endOff = 0;

  const mark = (node: Node, nodeStart: number, nodeEnd: number): void => {
    if (!startNode && start <= nodeEnd) {
      startNode = node;
      startOff = Math.max(0, start - nodeStart);
    }
    if (!endNode && end <= nodeEnd) {
      endNode = node;
      endOff = Math.max(0, end - nodeStart);
    }
  };

  const walk = (node: Node): void => {
    if (startNode && endNode) return;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const serializedLen = getTextSerializedLength(text);
      const nodeEnd = counted + serializedLen;
      mark(node, counted, nodeEnd);
      counted = nodeEnd;
      return;
    }

    if (!(node instanceof HTMLElement)) return;

    if (isEditorInlinePlaceholderNode(node)) return;

    if (isMentionToken(node)) {
      const serializedLen = getMentionSerializedLength(node);
      const nodeEnd = counted + serializedLen;
      if (start > counted && start < nodeEnd) {
        startNode = node.parentNode;
        startOff = Array.from(node.parentNode?.childNodes || []).indexOf(node) + 1;
      } else if (start === counted) {
        startNode = node.parentNode;
        startOff = Array.from(node.parentNode?.childNodes || []).indexOf(node);
      }
      if (end > counted && end < nodeEnd) {
        endNode = node.parentNode;
        endOff = Array.from(node.parentNode?.childNodes || []).indexOf(node) + 1;
      } else if (end === counted) {
        endNode = node.parentNode;
        endOff = Array.from(node.parentNode?.childNodes || []).indexOf(node);
      }
      counted = nodeEnd;
      return;
    }

    if (node.tagName === 'BR') {
      // <br> 不能当 Range 容器（insertNode 会丢），落在 parent 上 br 前/后。
      const parent = node.parentNode;
      const index = Array.from(parent?.childNodes || []).indexOf(node);
      const nodeEnd = counted + 1;
      if (!startNode && start <= nodeEnd) {
        startNode = parent;
        startOff = start <= counted ? index : index + 1;
      }
      if (!endNode && end <= nodeEnd) {
        endNode = parent;
        endOff = end <= counted ? index : index + 1;
      }
      counted = nodeEnd;
      return;
    }

    node.childNodes.forEach(walk);
  };

  root.childNodes.forEach(walk);

  if (!startNode) {
    startNode = root;
    startOff = root.childNodes.length;
  }
  if (!endNode) {
    endNode = root;
    endOff = root.childNodes.length;
  }

  const range = document.createRange();
  try {
    range.setStart(startNode, startOff);
    range.setEnd(endNode, endOff);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch {
    // 忽略无效选区
  }
}

export function createRangeAtOffsets(
  root: HTMLElement,
  start: number,
  end: number,
): Range {
  setSelectionOffsets(root, start, end);
  const sel = window.getSelection();
  const range = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : document.createRange();
  if (!sel?.rangeCount) {
    range.selectNodeContents(root);
    range.collapse(false);
  }
  return range;
}

/** 在 DOM 中按正文更新 mention（知识库多选替换） */
export function replaceMentionBodyInRoot(
  root: HTMLElement,
  oldBody: string,
  newBody: string,
  kind: TMentionEditorKind,
): void {
  const trimmedOld = oldBody.trim();
  const trimmedNew = newBody.trim();
  if (!trimmedOld) return;

  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    if (getMentionKind(el) !== kind) return;
    if ((el.getAttribute(MENTION_BODY_ATTR) || '').trim() !== trimmedOld) return;
    el.setAttribute(MENTION_BODY_ATTR, trimmedNew);
    const labelEl = el.querySelector('.dba-mention-token__label');
    if (labelEl) labelEl.textContent = trimmedNew;
  });
}

export function removeLabelFromRoot(
  root: HTMLElement,
  label: string,
  kind: TMentionEditorKind,
): void {
  const body = parseMentionBodyFromLabel(label, kind);
  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    if (getMentionKind(el) !== kind) return;
    if ((el.getAttribute(MENTION_BODY_ATTR) || '').trim() === body.trim()) {
      el.remove();
    }
  });
}

export function getCaretRectAtOffset(
  root: HTMLElement,
  offset: number,
): DOMRect | null {
  const sel = window.getSelection();
  if (!sel) return null;

  const saved: Range[] = [];
  for (let i = 0;i < sel.rangeCount;i += 1) {
    saved.push(sel.getRangeAt(i).cloneRange());
  }

  setSelectionOffsets(root, offset, offset);
  const rect = sel.rangeCount > 0
    ? sel.getRangeAt(0).getBoundingClientRect()
    : null;

  sel.removeAllRanges();
  saved.forEach((range) => sel.addRange(range));

  return rect;
}

/* ────────────────────────── mention 正文 / payload（locale 同步用） ────────────────────────── */

function parseKnowledgeMentionBody(
  items: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): string {
  const label = buildKnowledgeInlineLabel(items, kbDocUnit).trim();
  if (!label) return items[0]?.kbName || '';
  return label.startsWith('@') ? label.slice(1).trim() : label;
}

export function computeKnowledgeMentionBody(
  items: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): string {
  return parseKnowledgeMentionBody(items, kbDocUnit);
}

export function computeSkillMentionBody(
  skill: ISelectedSkillTagData,
  isEN: boolean,
): string {
  return resolveSelectedSkillDisplayName(skill, isEN);
}

function groupKnowledgeByKb(
  items: ISelectedKnowledgeTagData[],
): ISelectedKnowledgeTagData[][] {
  const byKb = new Map<string, ISelectedKnowledgeTagData[]>();
  items.forEach((item) => {
    const list = byKb.get(item.kbId) || [];
    list.push(item);
    byKb.set(item.kbId, list);
  });
  return Array.from(byKb.values());
}

/** 从 selectedKnowledge 匹配 mention 正文，用于 hydrate / locale 同步 */
export function matchKnowledgeItemsForBody(
  body: string,
  selected: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): ISelectedKnowledgeTagData[] {
  const trimmedBody = body.trim();
  if (!trimmedBody || !selected.length) return [];

  const groups = groupKnowledgeByKb(selected);
  const matched = groups.find((group) => {
    const label = buildKnowledgeInlineLabel(group, kbDocUnit).trim();
    const displayBody = label.startsWith('@') ? label.slice(1).trim() : label;
    return displayBody === trimmedBody;
  });
  if (matched) return matched;

  if (trimmedBody.includes('-')) {
    return selected.filter((item) => {
      if (!item.docName) return false;
      const kbPrefix = `${item.kbName}-`;
      if (!trimmedBody.startsWith(kbPrefix)) return false;
      const docNames = trimmedBody
        .slice(kbPrefix.length)
        .split('、')
        .map((name) => name.trim());
      return docNames.includes(item.docName);
    });
  }

  return [];
}

export function getMentionPayloadFromElement(el: HTMLElement): {
  knowledgeItems?: ISelectedKnowledgeTagData[];
  skillPayload?: ISelectedSkillTagData;
} {
  const raw = el.getAttribute(MENTION_PAYLOAD_ATTR);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return {
      knowledgeItems: parsed?.knowledgeItems,
      skillPayload: parsed?.skillPayload,
    };
  } catch {
    return {};
  }
}

export function setMentionPayloadOnElement(
  el: HTMLElement,
  payload: {
    knowledgeItems?: ISelectedKnowledgeTagData[];
    skillPayload?: ISelectedSkillTagData;
  },
): void {
  if (payload.knowledgeItems?.length) {
    el.setAttribute(MENTION_PAYLOAD_ATTR, JSON.stringify({ knowledgeItems: payload.knowledgeItems }));
  } else if (payload.skillPayload) {
    el.setAttribute(MENTION_PAYLOAD_ATTR, JSON.stringify({ skillPayload: payload.skillPayload }));
  }
}

/** 语言/单位切换时刷新知识库、Skill mention 展示文案（依赖各自 payload） */
export function relocalizeMentionsInRoot(
  root: HTMLElement,
  locale: IEditorLocaleContext,
): boolean {
  let changed = false;
  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    const kind = getMentionKind(el);
    if (!kind) return;
    const payload = getMentionPayloadFromElement(el);
    let nextBody: string | null = null;
    if (kind === 'knowledge' && payload.knowledgeItems?.length) {
      nextBody = computeKnowledgeMentionBody(payload.knowledgeItems, locale.kbDocUnit);
    } else if (kind === 'skill' && payload.skillPayload) {
      nextBody = computeSkillMentionBody(payload.skillPayload, locale.isEN);
    }
    if (nextBody == null) return;
    const current = el.getAttribute(MENTION_BODY_ATTR) || '';
    if (current === nextBody) return;
    el.setAttribute(MENTION_BODY_ATTR, nextBody);
    if (kind === 'skill') {
      el.setAttribute('data-mention-script', detectMentionScript(nextBody));
    }
    const labelEl = el.querySelector('.dba-mention-token__label');
    if (labelEl) labelEl.textContent = nextBody;
    changed = true;
  });
  return changed;
}

/** hydrate 后为尚无 payload 的知识库 mention 补上 items（用于 locale 同步 / 多文档合并展示） */
export function attachKnowledgePayloadsInRoot(
  root: HTMLElement,
  selected: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): void {
  if (!selected.length) return;
  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    if (getMentionKind(el) !== 'knowledge') return;
    if (el.hasAttribute(MENTION_PAYLOAD_ATTR)) return;
    const body = el.getAttribute(MENTION_BODY_ATTR) || '';
    const matched = matchKnowledgeItemsForBody(body, selected, kbDocUnit);
    if (!matched.length) return;
    setMentionPayloadOnElement(el, { knowledgeItems: matched });
  });
}

/** hydrate 后为尚无 payload 的 Skill mention 补上 skill 数据（locale 同步用） */
export function attachSkillPayloadsInRoot(
  root: HTMLElement,
  selected: ISelectedSkillTagData[],
  _isEN: boolean,
): void {
  if (!selected.length) return;
  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    if (getMentionKind(el) !== 'skill') return;
    if (el.hasAttribute(MENTION_PAYLOAD_ATTR)) return;
    const body = (el.getAttribute(MENTION_BODY_ATTR) || '').trim();
    if (!body) return;
    const matched = selected.find((skill) => {
      const names = [
        resolveSelectedSkillDisplayName(skill, false),
        resolveSelectedSkillDisplayName(skill, true),
        skill.name,
        skill.displayName,
      ]
        .map((name) => name?.trim())
        .filter(Boolean) as string[];
      return names.some((name) => name === body);
    });
    if (!matched) return;
    setMentionPayloadOnElement(el, { skillPayload: matched });
  });
}

/**
 * Backspace / Delete 时原子删除 mention（含尾随空格），避免退格拆解 tag 后误触发 `/` 下拉
 */
export function tryAtomicMentionDeleteInEditor(
  root: HTMLElement,
  key: 'Backspace' | 'Delete',
): { handled: boolean; nextValue: string; cursor: number; } | null {
  const inlineRanges = collectDomMentionRanges(root);
  if (!inlineRanges.length) return null;

  const serialized = serializeEditorRoot(root);
  const { start, end } = getSelectionOffsets(root);

  let hit: IInlineMentionRange | null = null;

  if (start !== end) {
    hit = inlineRanges.find((range) => {
      const mentionEnd = mentionEndOffset(serialized, range);
      return start === range.start && (end === mentionEnd || end === range.end);
    }) || null;
  } else if (key === 'Backspace') {
    hit = inlineRanges.find((range) => {
      const mentionEnd = mentionEndOffset(serialized, range);
      return start === mentionEnd || start === range.end;
    }) || null;
  } else {
    hit = inlineRanges.find((range) => start === range.start) || null;
  }

  if (!hit || !removeDomMentionToken(root, hit.key)) return null;

  const nextValue = serializeEditorRoot(root);
  const cursor = hit.start;
  setSelectionOffsets(root, cursor, cursor);
  return { handled: true, nextValue, cursor };
}

/* ────────────────────────── mention 插入 / 定位（DOM Range 直接操作） ────────────────────────── */

/** 用 [start,end) 序列化偏移替换为给定节点（mention 插入 / trigger 消费） */
export function insertNodeAtOffsets(
  root: HTMLElement,
  start: number,
  end: number,
  node: Node,
): void {
  const range = createRangeAtOffsets(root, start, end);
  range.deleteContents();
  range.insertNode(node);
  const after = document.createRange();
  after.setStartAfter(node);
  after.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(after);
}

/**
 * mention 插入前：若紧贴非空白正文或上一 mention，补前置空格，避免序列化粘连。
 */
/** 修复 DOM 中 mention 与相邻正文/mention 之间缺失的可编辑空格（与序列化规范一致） */
export function syncMentionTypingGapsInRoot(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`).forEach((el) => {
    ensureTypingSpaceBeforeMention(root, el);
    ensureTypingSpaceAfterMention(root, el, false);
  });
}

export function ensureTypingSpaceBeforeMention(
  root: HTMLElement,
  mentionNode: Node,
): void {
  if (!(mentionNode instanceof HTMLElement) || !isMentionToken(mentionNode)) {
    return;
  }

  const prev = mentionNode.previousSibling;
  if (prev?.nodeType === Node.TEXT_NODE) {
    const text = prev.textContent || '';
    if (text.length > 0 && !/\s$/.test(text)) {
      prev.textContent = `${text} `;
    }
    return;
  }

  if (prev instanceof HTMLElement && isMentionToken(prev)) {
    const space = document.createTextNode(' ');
    root.insertBefore(space, mentionNode);
  }
}

/**
 * mention 插入后补可编辑空格；可选将 caret 放入其中。
 * 避免光标贴在 contentEditable=false 的 tag 上继承 tag 行高。
 */
export function ensureTypingSpaceAfterMention(
  root: HTMLElement,
  mentionNode: Node,
  placeCaret = true,
): Text | null {
  if (!(mentionNode instanceof HTMLElement) || !isMentionToken(mentionNode)) {
    return null;
  }

  let caretNode: Text;

  const next = mentionNode.nextSibling;
  if (next?.nodeType === Node.TEXT_NODE) {
    caretNode = next as Text;
    const text = caretNode.textContent || '';
    if (!text.startsWith(' ')) {
      caretNode.textContent = ` ${text}`;
    }
  } else if (next instanceof HTMLElement && isEditorInlinePlaceholderNode(next)) {
    caretNode = document.createTextNode(' ');
    root.insertBefore(caretNode, next);
  } else {
    caretNode = document.createTextNode(' ');
    if (mentionNode.nextSibling) {
      root.insertBefore(caretNode, mentionNode.nextSibling);
    } else {
      root.appendChild(caretNode);
    }
  }

  if (placeCaret) {
    placeCaretInTextNode(caretNode, 1);
  }
  return caretNode;
}

/** 用 [start,end) 序列化偏移替换为纯文本（触发符消费但取消插入 mention 时使用） */
export function replaceOffsetsWithText(
  root: HTMLElement,
  start: number,
  end: number,
  text: string,
): void {
  const range = createRangeAtOffsets(root, start, end);
  range.deleteContents();
  if (!text) {
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    return;
  }
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  const after = document.createRange();
  after.setStart(textNode, textNode.length);
  after.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(after);
}

/** 在当前浏览器选区处插入纯文本（如 Shift+Enter 换行、粘贴） */
export function insertTextAtCurrentSelection(
  root: HTMLElement,
  text: string,
): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return;
  range.deleteContents();
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  const after = document.createRange();
  after.setStart(textNode, textNode.length);
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);
}

/** 把光标放在指定 mention token 的紧前/紧后（点击 tag 时使用） */
export function placeCaretAroundMention(
  root: HTMLElement,
  mentionKey: string,
  before: boolean,
): boolean {
  if (!mentionKey) return false;
  const el = Array.from(
    root.querySelectorAll<HTMLElement>(`.${MENTION_TOKEN_CLASS}`),
  ).find((token) => token.getAttribute(MENTION_KEY_ATTR) === mentionKey);
  if (!el) return false;
  const range = document.createRange();
  if (before) {
    range.setStartBefore(el);
  } else {
    range.setStartAfter(el);
  }
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
  return true;
}

/** 超出 maxLength 时从末尾裁剪（mention 原子节点整体移除，不做半截断） */
export function trimRootToLength(root: HTMLElement, maxLength: number): void {
  let current = serializeEditorRoot(root);
  if (current.length <= maxLength) return;
  let remaining = current.length - maxLength;
  let node: Node | null = root.lastChild;

  while (node && remaining > 0) {
    const prev = node.previousSibling;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const len = getTextSerializedLength(text);
      if (len <= remaining) {
        remaining -= len;
        (node as ChildNode).remove();
      } else {
        node.textContent = text.slice(0, text.length - remaining);
        remaining = 0;
      }
    } else if (node instanceof HTMLElement && isMentionToken(node)) {
      remaining -= getMentionSerializedLength(node);
      node.remove();
    } else if (node instanceof HTMLElement && node.tagName === 'BR') {
      remaining -= 1;
      node.remove();
    } else {
      (node as ChildNode).remove();
    }
    node = prev;
  }

  current = serializeEditorRoot(root);
  setSelectionOffsets(root, current.length, current.length);
}
